/**
 * @voltxampere/engine — One-shot MNA index plan
 *
 * Newton-Raphson rebuilds the matrix every iteration, but the
 * *index plan* (which net is row 0, which voltage source is M-slot 2,
 * etc.) is derived from the circuit topology and never changes
 * between iterations. Splitting the plan out lets the iteration
 * loop reuse it for free instead of recomputing it 5–20 times per
 * solve.
 *
 * The plan also encodes which components contribute an augmented
 * row to the MNA system. Per SPECS §3.1, that's any independent
 * voltage source. SPECS §3.5–3.8 pickup: in DC, an inductor reduces
 * to a 0-volt source (short circuit) and therefore also adds an
 * augmented row. Capacitors are open at DC and do not.
 *
 * Sprint 6 Smell #8 fix: the planner consults the stamp registry
 * (`getStampAugmentation(type)`) instead of carrying its own hard-
 * coded `AUGMENTED_TYPES` set. Stamps declare their own augmentation
 * needs in their {@link DCStampDefinition}, so adding a new type
 * that needs an augmented row is now a single-file change. Calling
 * `planCircuit` before `registerDCStamps()` therefore throws a
 * `ContractViolationError` for any unknown type — the planner used
 * to silently treat unknowns as non-augmented, which would corrupt
 * the matrix size for circuits containing those types.
 *
 * Validation:
 *   - exactly one ground net (per SPECS §1.2 invariant);
 *   - every net id referenced by a terminal exists in `circuit.nets`;
 *   - every component type has a registered stamp definition.
 *
 * Errors thrown here surface as `ValidationGroundMissingError` (no
 * ground), `ValidationNetMissingError` (dangling terminal), or
 * `ContractViolationError` (more than one ground — should have been
 * caught by schema, included as defence-in-depth — or stamp registry
 * not populated).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ContractViolationError, ValidationGroundMissingError, ValidationNetMissingError, } from "@voltxampere/shared";
import { getDCStampDefinition, getStampAugmentation } from "../stamps/register.js";
/** True iff the type's registered stamp declares the `"vsource"` augmentation. */
function hasVSourceAugmentation(type) {
    const augs = getStampAugmentation(type);
    if (augs === undefined) {
        throw new ContractViolationError(`planCircuit: no stamp definition registered for type "${type}" — call registerDCStamps() before planCircuit()`);
    }
    return augs.includes("vsource");
}
/**
 * Sprint 24 B1 — resolve the full augmentation list for a component
 * instance, concatenating static `augments` with the optional
 * `dynamicAugments(c)` result. Returns the combined list in static-
 * first / dynamic-second order so allocator indices are stable.
 */
function resolveAugments(c) {
    const def = getDCStampDefinition(c.type);
    if (def === undefined) {
        throw new ContractViolationError(`planCircuit: no stamp definition registered for type "${c.type}" — call registerDCStamps() before planCircuit()`);
    }
    if (def.dynamicAugments === undefined) {
        return def.augments;
    }
    const dynamic = def.dynamicAugments(c);
    if (dynamic.length === 0)
        return def.augments;
    return [...def.augments, ...dynamic];
}
export function planCircuit(circuit) {
    // 1. Identify ground.
    const grounds = circuit.nets.filter((n) => n.isGround);
    if (grounds.length === 0) {
        throw new ValidationGroundMissingError(`circuit "${circuit.id}" has no net with isGround=true`);
    }
    if (grounds.length > 1) {
        throw new ContractViolationError(`planCircuit: circuit "${circuit.id}" has ${String(grounds.length)} ground nets — exactly one is required (SPECS §1.2)`);
    }
    const groundNet = grounds[0];
    if (groundNet === undefined) {
        // Unreachable: length === 1 was just checked above. Defensive
        // guard for the type system.
        throw new ContractViolationError("planCircuit: ground net vanished after length check");
    }
    const groundNetId = groundNet.id;
    // 2. Index ground-excluded nets in declaration order.
    const netToIndex = new Map();
    const orderedNetIds = [];
    for (const net of circuit.nets) {
        if (net.id === groundNetId)
            continue;
        netToIndex.set(net.id, orderedNetIds.length);
        orderedNetIds.push(net.id);
    }
    // 3. Validate every terminal references a known net.
    const knownNetIds = new Set(circuit.nets.map((n) => n.id));
    for (const c of circuit.components) {
        for (const t of c.terminals) {
            if (!knownNetIds.has(t.netId)) {
                throw new ValidationNetMissingError(`component "${c.id}" terminal "${t.terminalId}" references unknown net "${t.netId}"`, { meta: { componentId: c.id, terminalId: t.terminalId, netId: t.netId } });
            }
        }
    }
    // 4. Allocate augmented rows. Each component asks the stamp
    // registry whether its type contributes a `"vsource"`-style
    // augmentation; the planner no longer maintains its own list
    // (Sprint 6 Smell #8 fix).
    //
    // Sprint 24 B1 — also allocate `"internalNode"` augmentations
    // (BJT parazitik B'/C'/E' internal terminals). Internal nodes
    // come BEFORE vsource rows in the MNA layout: the system block
    // structure is [node-KCL | internal-KCL | vsource-augmented].
    // This keeps the Sprint 8b reduced-residual partition consistent
    // — internal nodes are KCL-bearing rows just like external
    // nodes, so they live in the `nodeIndexSet` block with the same
    // KCL convergence semantics.
    const baseNodeCount = orderedNetIds.length;
    const internalNodeIndices = new Map();
    let internalNodeCursor = baseNodeCount;
    const orderedDynamicAugmentsByComponent = [];
    for (const c of circuit.components) {
        const augs = resolveAugments(c);
        orderedDynamicAugmentsByComponent.push({ id: c.id, augs });
        const internalCount = augs.filter((a) => a === "internalNode").length;
        if (internalCount === 0)
            continue;
        const indices = [];
        for (let s = 0; s < internalCount; s++) {
            indices.push(internalNodeCursor);
            internalNodeCursor++;
        }
        internalNodeIndices.set(c.id, indices);
    }
    const internalNodeCount = internalNodeCursor - baseNodeCount;
    const vSourceIdToIndex = new Map();
    const orderedVSourceIds = [];
    for (const c of circuit.components) {
        if (hasVSourceAugmentation(c.type)) {
            vSourceIdToIndex.set(c.id, orderedVSourceIds.length);
            orderedVSourceIds.push(c.id);
        }
    }
    // 5. Sprint 7.1 P1-1: detect floating MOSFET gates. The Shichman-
    // Hodges stamp stamps zero admittance into the gate row / col (the
    // ideal MOSFET draws no DC gate current), so a gate net wired only
    // to the MOSFET itself produces a singular MNA matrix. Catch this
    // at plan time with a clear error rather than surfacing it as a
    // cryptic LU pivot failure deep inside the solver.
    detectFloatingMosfetGates(circuit);
    // 6. Sprint 8a: collect junction-bearing MNA indices. Walk every
    // component, read its stamp's declared junction terminals, look
    // up the attached net, and record the MNA row index. Ground-
    // connected terminals are silently skipped (no row exists).
    //
    // Sprint 54.3h engine ext #10: also collect volatile MNA indices
    // in the same pass. Sources: (a) `StampDefinition.volatileTerminals`
    // → terminal-attached net index; (b) every internal-node augment
    // row added in step 4 (always volatile-by-default — state rows
    // are pinned identity equations, LTE is meaningless on them).
    const junctionBearingIndices = new Set();
    const volatileIndices = new Set();
    // (b) — internal node rows are always volatile.
    for (const indices of internalNodeIndices.values()) {
        for (const idx of indices)
            volatileIndices.add(idx);
    }
    for (const c of circuit.components) {
        const def = getDCStampDefinition(c.type);
        if (def === undefined) {
            // Already caught by the augmentation check at step 4, but the
            // type-system doesn't know that; defensive skip.
            continue;
        }
        const junctionTerms = def.junctionTerminals;
        if (junctionTerms !== undefined && junctionTerms.length > 0) {
            for (const termId of junctionTerms) {
                const term = c.terminals.find((t) => t.terminalId === termId);
                if (term === undefined) {
                    throw new ContractViolationError(`planCircuit: component "${c.id}" (type "${c.type}") is missing declared junction terminal "${termId}"`);
                }
                const idx = netToIndex.get(term.netId);
                if (idx === undefined)
                    continue; // ground: not in the MNA vector
                junctionBearingIndices.add(idx);
            }
        }
        // Sprint 54.3h — (a) volatileTerminals → net index.
        const volTerms = def.volatileTerminals;
        if (volTerms !== undefined && volTerms.length > 0) {
            for (const termId of volTerms) {
                const term = c.terminals.find((t) => t.terminalId === termId);
                if (term === undefined) {
                    throw new ContractViolationError(`planCircuit: component "${c.id}" (type "${c.type}") is missing declared volatile terminal "${termId}"`);
                }
                const idx = netToIndex.get(term.netId);
                if (idx === undefined)
                    continue; // ground: not in the MNA vector
                volatileIndices.add(idx);
            }
        }
    }
    const nodeCount = orderedNetIds.length;
    const vSourceCount = orderedVSourceIds.length;
    // Sprint 24 B1 — total system size includes internal node block.
    // Layout: [external-KCL | internal-KCL | vsource-augmented].
    const size = nodeCount + internalNodeCount + vSourceCount;
    // Sprint 8b: partition MNA row indices into node-KCL block
    // `[0, nodeCount)` and augmented block `[nodeCount, size)`. Sealed
    // once here so the Newton driver and Anderson-II fallback can
    // re-use the same sets without re-deriving from nodeCount.
    //
    // Sprint 24 B1: internal nodes (Sprint 24 B1) join the KCL block
    // — they obey ordinary KCL (sum-of-currents = 0) just like
    // external nets. Augmented block keeps only vsource rows.
    const nodeIndexSet = new Set();
    for (let i = 0; i < nodeCount + internalNodeCount; i++)
        nodeIndexSet.add(i);
    const augmentedIndexSet = new Set();
    for (let i = nodeCount + internalNodeCount; i < size; i++)
        augmentedIndexSet.add(i);
    return {
        groundNetId,
        netToIndex,
        vSourceIdToIndex,
        orderedNetIds,
        orderedVSourceIds,
        nodeCount,
        vSourceCount,
        internalNodeIndices,
        internalNodeCount,
        size,
        junctionBearingIndices,
        nodeIndexSet,
        augmentedIndexSet,
        volatileIndices,
    };
}
/**
 * Type guard for components that own an augmented MNA row. Reads
 * the registry, so `registerDCStamps()` must run before this is
 * called (otherwise it throws `ContractViolationError`).
 */
export function isAugmentedComponent(component) {
    return hasVSourceAugmentation(component.type);
}
/**
 * Sprint 7.1 P1-1: walk the component list and verify every MOSFET
 * gate net is connected to at least one other component (or is the
 * ground net). A gate wired only to the MOSFET itself has no DC
 * path and the Shichman-Hodges stamp's zero admittance in the gate
 * row collapses the MNA matrix.
 *
 * Ground-connected gates are accepted (the MOSFET is pinned off).
 * Otherwise the gate net must appear on at least one NON-MOSFET
 * component's terminal — a pull-up/pull-down resistor, a voltage
 * source, another MOSFET's drain/source/gate, etc. — so the system
 * has a way to determine V_gate.
 */
function detectFloatingMosfetGates(circuit) {
    const MOSFET_TYPES = new Set(["mosfet-nmos", "mosfet-pmos"]);
    const groundNetId = circuit.nets.find((n) => n.isGround)?.id;
    // Build net → non-mosfet-gate-only references map. A gate is
    // "connected" if at least one other terminal on some component
    // touches the same net.
    for (const c of circuit.components) {
        if (!MOSFET_TYPES.has(c.type))
            continue;
        const gateTerm = c.terminals.find((t) => t.terminalId === "gate");
        if (gateTerm === undefined)
            continue;
        const gateNetId = gateTerm.netId;
        if (gateNetId === groundNetId)
            continue; // ground is a valid pin
        // Look for any OTHER terminal on ANY component that shares this
        // net id. A MOSFET's own gate pin doesn't count as an external
        // connection.
        let connected = false;
        outer: for (const other of circuit.components) {
            for (const t of other.terminals) {
                if (t.netId !== gateNetId)
                    continue;
                // Same MOSFET's gate terminal doesn't count.
                if (other.id === c.id && t.terminalId === "gate")
                    continue;
                connected = true;
                break outer;
            }
        }
        if (!connected) {
            throw new ContractViolationError(`planCircuit: MOSFET "${c.id}" has a floating gate (net "${gateNetId}" has no other DC path). ` +
                `A Shichman-Hodges stamp puts zero admittance on the gate row, so a DC solve with a floating gate ` +
                `produces a singular matrix. Tie the gate to a voltage source, a resistor divider, or ground.`);
        }
    }
}
//# sourceMappingURL=circuit-plan.js.map